import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BackButton, HomeButton } from '../components/NavButtons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Country } from 'country-state-city';
import axios from 'axios';
import {
  VideoCamera, Users, Clock, CalendarBlank, Plus, X, Play,
  HandHeart, Link as LinkIcon, Warning, Lightning, Stop, Globe, MagnifyingGlass, Funnel, MapPin
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AddToCalendar from '../components/AddToCalendar';
import StudioSettingsCard from '../components/StudioSettingsCard';
import VoiceSearchButton from '../components/VoiceSearchButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_SESSION_TYPES = ['Hatha Yoga', 'Vinyasa', 'Power Yoga', 'Meditation', 'Pranayama', 'Sound Healing', 'Pilates', 'Tai Chi', 'Breathwork', 'Stretching'];

const EMPTY_FORM = {
  title: '', description: '', instructor: '', session_type: 'Hatha Yoga',
  date: '', start_time: '09:00', end_time: '10:00', duration_minutes: 60,
  max_participants: 10, price: '', currency: 'INR',
  meeting_type: 'jitsi', external_meeting_link: '',
  country: '', // single ISO code; '' = Global
};

const LiveClasses = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isSeller } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'subadmin');
  const isPureSeller = user && user.role === 'seller'; // pure seller (not 'both'/'admin'): hide other sellers' classes from seller portal
  const categoryFilter = searchParams.get('category') || '';
  const autoBookId = searchParams.get('autobook') || '';
  const [classes, setClasses] = useState([]);
  const [myClasses, setMyClasses] = useState({ seller_classes: [], booked_classes: [] });
  const [sessionTypes, setSessionTypes] = useState(FALLBACK_SESSION_TYPES);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState(user && user.role === 'seller' ? 'hosted' : 'browse');
  const [form, setForm] = useState(EMPTY_FORM);
  const [browseCountry, setBrowseCountry] = useState(''); // buyer-side filter; '' = global (all)
  const [searchQuery, setSearchQuery] = useState('');

  const canHost = !!eligibility?.eligible;

  const fetchClasses = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status: 'upcoming' });
      if (categoryFilter) params.set('category', categoryFilter);
      // Manual filter takes precedence; otherwise auto-use buyer's profile country
      if (browseCountry) {
        params.set('country', browseCountry);
      } else if (user && !isAdmin && !isSeller && user.country) {
        params.set('country', user.country);
      }
      const res = await axios.get(`${API_URL}/api/live-classes?${params.toString()}`);
      setClasses(res.data.classes || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [user, isAdmin, isSeller, browseCountry, categoryFilter]);

  const fetchMyClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/live-classes/my`);
      setMyClasses(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const eligibilityCategory = eligibility?.category;
  const fetchSessionTypes = useCallback(async (cat) => {
    try {
      const useCat = cat || eligibilityCategory || 'yoga_wellness';
      const res = await axios.get(`${API_URL}/api/session-types?category=${useCat}`);
      const list = (res.data?.session_types || []).map(s => s.name).filter(Boolean);
      if (list.length) setSessionTypes(list);
      else setSessionTypes(FALLBACK_SESSION_TYPES);
    } catch (err) { console.warn('Session types fetch failed, using fallback:', err?.message); }
  }, [eligibilityCategory]);

  const fetchEligibility = useCallback(async () => {
    if (!user) { setEligibility(null); return; }
    if (!isSeller && !isAdmin) { setEligibility({ eligible: false, reason: 'not_seller' }); return; }
    try {
      const res = await axios.get(`${API_URL}/api/live-classes/eligibility`);
      setEligibility(res.data);
    } catch (err) { setEligibility({ eligible: false, reason: 'error' }); }
  }, [user, isSeller, isAdmin]);

  useEffect(() => {
    // Pure sellers: don't fetch other sellers' classes; only fetch their own + their bookings.
    if (!isPureSeller) fetchClasses();
    if (user) {
      fetchMyClasses();
      fetchEligibility();
    }
  }, [user, isPureSeller, fetchClasses, fetchMyClasses, fetchEligibility]);

  // Session types depend only on the (stable) eligibility category — kept in its own
  // effect so eligibility object identity changes can't re-trigger the fetch loop.
  useEffect(() => {
    fetchSessionTypes();
  }, [fetchSessionTypes]);

  // Auto-correct tab state if a pure seller is somehow on the (hidden) browse tab
  useEffect(() => {
    if (isPureSeller && tab === 'browse') setTab('hosted');
  }, [isPureSeller, tab]);

  // After-login auto-book flow: ?autobook=class_id
  const autobookFiredRef = useRef(false);
  useEffect(() => {
    if (!user || !autoBookId || autobookFiredRef.current) return;
    autobookFiredRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/live-classes/${autoBookId}/book`);
        if (cancelled) return;
        toast.success(res.data?.message || 'Booking confirmed!');
        fetchClasses();
        fetchMyClasses();
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Could not auto-book the class');
      } finally {
        // Clean the autobook param from the URL so refresh doesn't re-trigger
        const next = new URLSearchParams(searchParams);
        next.delete('autobook');
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, autoBookId]);

  const handleCreate = async () => {
    if (!form.title || !form.date) { toast.error('Title and date are required'); return; }
    if (form.meeting_type === 'in_person' && !form.venue_address.trim()) { toast.error('Venue address is required for in-person sessions'); return; }
    if (form.meeting_type === 'external' && !form.external_meeting_link.trim()) { toast.error('Meeting link is required for external sessions'); return; }
    try {
      await axios.post(`${API_URL}/api/live-classes`, { ...form, category: eligibility?.category || 'yoga_wellness' });
      toast.success('Live class created!');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchClasses();
      fetchMyClasses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const handleBook = async (classId) => {
    if (!user) {
      // Send buyer to login with a redirect back to /live-classes (and auto-book the class on return)
      navigate(`/buyer/login?redirect=${encodeURIComponent('/live-classes')}&autobook=${classId}`);
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/live-classes/${classId}/book`);
      toast.success(res.data.message);
      fetchClasses();
      fetchMyClasses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Booking failed'); }
  };

  const handleCancel = async (classId) => {
    try {
      await axios.post(`${API_URL}/api/live-classes/${classId}/cancel`);
      toast.success('Booking cancelled');
      fetchClasses();
      fetchMyClasses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cancel failed'); }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await axios.delete(`${API_URL}/api/live-classes/${classId}`);
      toast.success('Session deleted');
      fetchClasses();
      fetchMyClasses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleGoLive = async (cls) => {
    if ((cls.booked_count || 0) < 1) {
      toast.error('You can go live only after at least one buyer has subscribed');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/live-classes/${cls.class_id}/go-live`);
      toast.success('You are now LIVE');
      fetchMyClasses();
      navigate(`/live-classes/${cls.class_id}/join`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not go live'); }
  };

  const handleEnd = async (cls) => {
    if (!window.confirm('End this live session?')) return;
    try {
      await axios.post(`${API_URL}/api/live-classes/${cls.class_id}/end`);
      toast.success('Session ended');
      fetchMyClasses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to end'); }
  };

  const isBooked = (cls) => user && cls.participants?.includes(user.user_id);
  const isMyClass = (cls) => user && cls.seller_id === user.user_id;

  const TABS = [
    // Pure sellers don't see "Browse Classes" — their portal only shows their own + their personal bookings.
    ...(isPureSeller ? [] : [{ key: 'browse', label: 'Browse Sessions' }]),
    ...(user ? [{ key: 'my', label: 'My Bookings' }] : []),
    ...((isSeller || isAdmin) && canHost ? [{ key: 'hosted', label: 'My Sessions' }] : []),
  ];

  // Visible classes (browse) — for sellers, hide own classes from the public browse list; also apply text search
  const browseClasses = useMemo(() => {
    let list = classes.filter(c => !(user && c.seller_id === user.user_id));
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(c =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.session_type || '').toLowerCase().includes(q) ||
        (c.seller_name || '').toLowerCase().includes(q) ||
        (c.instructor || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        (c.category || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [classes, user, searchQuery]);

  // Unique categories present in the loaded classes (for the dropdown)
  const browseCategoryOptions = useMemo(() => {
    const seen = new Set();
    classes.forEach(c => { if (c.category) seen.add(c.category); });
    return Array.from(seen).sort();
  }, [classes]);

  const setCategoryParam = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (cat) next.set('category', cat); else next.delete('category');
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BackButton />
            <HomeButton />
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="live-classes-title">
              <HandHeart size={22} weight="fill" className="text-pink-500" /> Live Sessions
            </h1>
          </div>
          {(isSeller || isAdmin) && canHost && (
            <Button onClick={() => { setShowCreate(true); setTab('hosted'); }}
              className="bg-pink-500 hover:bg-pink-600 rounded-xl h-9 text-sm" data-testid="create-class-btn">
              <Plus size={16} className="mr-1.5" /> Create Session
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-5">
        {/* Eligibility Banner for sellers without an eligible listing */}
        {isSeller && !isAdmin && eligibility && !eligibility.eligible && (eligibility.reason === 'no_yoga_listing' || eligibility.reason === 'no_eligible_listing') && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 flex-wrap" data-testid="eligibility-banner">
            <Warning size={22} className="text-amber-500 mt-0.5 shrink-0" weight="fill" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">Live Sessions need an eligible listing</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Add an active listing in any of these categories to unlock hosting Live Sessions:{' '}
                <span className="font-semibold">
                  {(eligibility.categories || []).map(c => c.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())).join(', ') || 'Yoga / Wellness'}
                </span>
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(eligibility.categories || ['yoga_wellness']).slice(0, 3).map(cat => (
                <Button key={cat} size="sm" onClick={() => navigate(`/seller/add-listing?category=${cat}`)}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg h-8 text-xs"
                  data-testid={`add-listing-btn-${cat}`}>
                  Add {cat.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* AI Studio (sellers/admin with eligibility) */}
        {(isSeller || isAdmin) && canHost && (
          <StudioSettingsCard />
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200" data-testid="class-tabs">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${tab === t.key ? 'bg-pink-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Create Form (sellers/admin with eligibility only) */}
        {showCreate && tab === 'hosted' && canHost && (
          <div className="bg-white rounded-xl border border-pink-200 p-5" data-testid="create-class-form">
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Create Live Session</h3>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Class Title *</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Morning Yoga Flow" className="h-9 rounded-lg text-sm" data-testid="class-title-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What will participants learn?" className="min-h-[60px] rounded-lg text-sm" data-testid="class-desc-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Instructor</Label>
                <Input value={form.instructor} onChange={e => setForm({ ...form, instructor: e.target.value })} placeholder="Instructor name" className="h-9 rounded-lg text-sm" data-testid="class-instructor-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Session Type</Label>
                <select value={form.session_type} onChange={e => setForm({ ...form, session_type: e.target.value })} className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="class-session-type">
                  {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="class-date-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Start</Label>
                  <Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="class-start-time" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">End</Label>
                  <Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="class-end-time" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Max Participants *</Label>
                <Input type="number" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: e.target.value })} min="1" className="h-9 rounded-lg text-sm" data-testid="class-max-participants" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Price</Label>
                <Input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="0 for free" className="h-9 rounded-lg text-sm" data-testid="class-price-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500 mb-2 block">Meeting Type</Label>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setForm({ ...form, meeting_type: 'jitsi' })}
                    className={`flex-1 min-w-[140px] p-3 rounded-xl border-2 text-center text-xs font-semibold transition-all ${form.meeting_type === 'jitsi' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-500'}`}
                    data-testid="meeting-type-jitsi">
                    <VideoCamera size={20} className="mx-auto mb-1" weight="fill" />
                    Built-in Video Room
                    <p className="text-[10px] font-normal mt-0.5 text-slate-400">Auto-generated Jitsi room</p>
                  </button>
                  <button onClick={() => setForm({ ...form, meeting_type: 'external' })}
                    className={`flex-1 min-w-[140px] p-3 rounded-xl border-2 text-center text-xs font-semibold transition-all ${form.meeting_type === 'external' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-500'}`}
                    data-testid="meeting-type-external">
                    <LinkIcon size={20} className="mx-auto mb-1" weight="bold" />
                    External Link
                    <p className="text-[10px] font-normal mt-0.5 text-slate-400">Google Meet / Zoom</p>
                  </button>
                  <button onClick={() => setForm({ ...form, meeting_type: 'in_person' })}
                    className={`flex-1 min-w-[140px] p-3 rounded-xl border-2 text-center text-xs font-semibold transition-all ${form.meeting_type === 'in_person' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-500'}`}
                    data-testid="meeting-type-in-person">
                    <MapPin size={20} className="mx-auto mb-1" weight="fill" />
                    In-Person (Offline)
                    <p className="text-[10px] font-normal mt-0.5 text-slate-400">Meet at your venue</p>
                  </button>
                </div>
              </div>
              {form.meeting_type === 'external' && (
                <div className="sm:col-span-2">
                  <Label className="text-xs text-slate-500 mb-1 block">Meeting Link *</Label>
                  <Input value={form.external_meeting_link} onChange={e => setForm({ ...form, external_meeting_link: e.target.value })} placeholder="https://meet.google.com/abc-defg-hij" className="h-9 rounded-lg text-sm" data-testid="class-meeting-link" />
                </div>
              )}
              {form.meeting_type === 'in_person' && (
                <div className="sm:col-span-2">
                  <Label className="text-xs text-slate-500 mb-1 block">Venue Address *</Label>
                  <Input value={form.venue_address} onChange={e => setForm({ ...form, venue_address: e.target.value })} placeholder="Shop 12, Astro Plaza, MG Road, Bengaluru" className="h-9 rounded-lg text-sm" data-testid="class-venue-address" />
                  <p className="text-[10px] text-slate-400 mt-1">Shown to booked participants so they can attend in person.</p>
                </div>
              )}
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Country (Timezone) *</Label>
                <p className="text-[11px] text-slate-400 mb-1.5">Select the country this class is for. Timing will be anchored to this country's timezone. Buyers from other countries can still see it.</p>
                <ClassCountrySelect
                  selected={form.country}
                  onChange={(code) => setForm({ ...form, country: code })}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleCreate} className="bg-pink-500 hover:bg-pink-600 rounded-xl text-sm" data-testid="submit-class-btn">Create Session</Button>
              <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-xl text-sm">Cancel</Button>
            </div>
          </div>
        )}

        {/* Browse Classes */}
        {tab === 'browse' && (
          <>
            {/* Search + Filters Bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 shadow-sm" data-testid="live-search-bar">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search input */}
                <div className="relative flex-1 min-w-[220px]">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title, seller name, session type, category..."
                    className="w-full h-10 pl-9 pr-9 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none"
                    data-testid="live-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      data-testid="live-search-clear"
                      aria-label="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {/* Voice */}
                <VoiceSearchButton onResult={(text) => setSearchQuery(text)} size="md" className="" />
                {/* Category */}
                <div className="flex items-center gap-1.5">
                  <Funnel size={14} weight="bold" className="text-slate-500" />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryParam(e.target.value)}
                    className="h-10 px-3 pr-8 border border-slate-200 rounded-xl text-sm text-slate-700 bg-white focus:border-pink-400 outline-none"
                    data-testid="live-category-select"
                  >
                    <option value="">All categories</option>
                    {browseCategoryOptions.map(cat => (
                      <option key={cat} value={cat}>{cat.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
                {/* Country */}
                <div className="flex items-center gap-1.5">
                  <Globe size={14} weight="bold" className="text-slate-500" />
                  <CountrySingleSelect selected={browseCountry} onChange={setBrowseCountry} />
                </div>
              </div>
              {/* Active filters chips */}
              {(searchQuery || categoryFilter || browseCountry) && (
                <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-slate-100 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Active:</span>
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-50 text-pink-700 rounded-full text-[11px] font-medium hover:bg-pink-100" data-testid="active-chip-search">
                      "{searchQuery}" <X size={10} />
                    </button>
                  )}
                  {categoryFilter && (
                    <button onClick={() => setCategoryParam('')} className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full text-[11px] font-medium hover:bg-violet-100" data-testid="active-chip-category">
                      {categoryFilter.replace(/_/g, ' ')} <X size={10} />
                    </button>
                  )}
                  {browseCountry && (
                    <button onClick={() => setBrowseCountry('')} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[11px] font-medium hover:bg-blue-100" data-testid="active-chip-country">
                      {browseCountry} <X size={10} />
                    </button>
                  )}
                  <button onClick={() => { setSearchQuery(''); setCategoryParam(''); setBrowseCountry(''); }}
                    className="ml-auto text-[11px] text-slate-500 hover:text-slate-800 font-semibold" data-testid="clear-all-filters">
                    Clear all
                  </button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={`lc-skel-${i}`} className="bg-white rounded-xl p-5 animate-pulse"><div className="h-4 bg-slate-200 rounded w-1/3 mb-2" /><div className="h-3 bg-slate-200 rounded w-1/2" /></div>)}</div>
            ) : browseClasses.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {browseClasses.map(cls => (
                  <ClassCard key={cls.class_id} cls={cls} user={user} isBooked={isBooked(cls)} isMyClass={isMyClass(cls)}
                    onBook={() => handleBook(cls.class_id)} onCancel={() => handleCancel(cls.class_id)}
                    onJoin={() => navigate(`/live-classes/${cls.class_id}/join`)} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
                <HandHeart size={48} className="text-slate-300 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-slate-700 mb-1">
                  {(searchQuery || categoryFilter) ? 'No classes match your search' : 'No upcoming live sessiones'}
                </h3>
                <p className="text-sm text-slate-400">
                  {(searchQuery || categoryFilter)
                    ? 'Try a different keyword or remove filters.'
                    : (browseCountry
                      ? `No classes available for the selected country. Try "All Countries".`
                      : (canHost ? 'Create your first live session!' : 'Check back soon for new classes'))}
                </p>
              </div>
            )}
          </>
        )}

        {/* My Booked Classes (Buyer view) */}
        {tab === 'my' && (
          myClasses.booked_classes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {myClasses.booked_classes.map(cls => (
                <ClassCard key={cls.class_id} cls={cls} user={user} isBooked={true} isMyClass={false}
                  onCancel={() => handleCancel(cls.class_id)}
                  onJoin={() => navigate(`/live-classes/${cls.class_id}/join`)} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
              <CalendarBlank size={40} className="text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No booked classes yet</p>
            </div>
          )
        )}

        {/* My Hosted Classes (Seller) */}
        {tab === 'hosted' && canHost && !showCreate && (
          myClasses.seller_classes.length > 0 ? (
            <div className="space-y-3">
              {myClasses.seller_classes.map(cls => (
                <HostedClassRow key={cls.class_id} cls={cls}
                  onGoLive={() => handleGoLive(cls)} onEnd={() => handleEnd(cls)}
                  onJoin={() => navigate(`/live-classes/${cls.class_id}/join`)}
                  onDelete={() => handleDelete(cls.class_id)} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
              <VideoCamera size={40} className="text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No classes created yet</p>
              <Button onClick={() => setShowCreate(true)} className="bg-pink-500 hover:bg-pink-600 rounded-xl mt-3 text-sm">
                <Plus size={14} className="mr-1.5" /> Create Your First Class
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const HostedClassRow = ({ cls, onGoLive, onEnd, onJoin, onDelete }) => {
  const hasSubscriber = (cls.booked_count || 0) >= 1;
  const isLive = cls.status === 'live';
  const isCompleted = cls.status === 'completed';
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`hosted-class-${cls.class_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-slate-900 text-sm">{cls.title}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{cls.session_type} | {cls.date} | {cls.start_time} - {cls.end_time}</p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-xs font-semibold text-pink-600">{cls.booked_count}/{cls.max_participants} booked</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isLive ? 'bg-red-100 text-red-700 animate-pulse' : isCompleted ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
              {isLive ? 'LIVE NOW' : isCompleted ? 'COMPLETED' : 'UPCOMING'}
            </span>
            {!hasSubscriber && !isLive && !isCompleted && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700 inline-flex items-center gap-1" data-testid={`waiting-subscriber-${cls.class_id}`}>
                <Warning size={10} weight="fill" /> Waiting for first subscriber
              </span>
            )}
            <VisibilityChip country={cls.country} countries={cls.visible_countries} compact />
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {isCompleted ? (
            <span className="text-xs text-slate-400">Session ended</span>
          ) : isLive ? (
            <>
              <Button size="sm" onClick={onJoin}
                className="bg-pink-500 hover:bg-pink-600 rounded-lg text-xs h-8" data-testid={`rejoin-class-${cls.class_id}`}>
                <Play size={12} className="mr-1" weight="fill" /> Rejoin
              </Button>
              <Button size="sm" onClick={onEnd} variant="outline"
                className="border-red-200 text-red-500 hover:bg-red-50 rounded-lg text-xs h-8" data-testid={`end-class-${cls.class_id}`}>
                <Stop size={12} className="mr-1" weight="fill" /> End
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={onGoLive} disabled={!hasSubscriber}
              className={`rounded-lg text-xs h-8 ${hasSubscriber ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
              data-testid={`go-live-${cls.class_id}`}
              title={hasSubscriber ? 'Start live session' : 'Wait for at least 1 buyer to subscribe'}>
              <Lightning size={12} className="mr-1" weight="fill" /> Go Live
            </Button>
          )}
          <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" data-testid={`delete-hosted-${cls.class_id}`}>
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const ClassCard = ({ cls, user, isBooked, isMyClass, onBook, onCancel, onJoin }) => {
  const remaining = cls.max_participants - cls.booked_count;
  const isFull = remaining <= 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`class-card-${cls.class_id}`}>
      <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-4 text-white">
        <div className="flex justify-between items-start">
          <div>
            <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-medium">{cls.session_type}</span>
            <h3 className="font-bold text-base mt-1.5">{cls.title}</h3>
            <p className="text-xs text-white/80 mt-0.5">{cls.instructor && `by ${cls.instructor}`}</p>
          </div>
          {cls.meeting_type === 'jitsi' ? (
            <VideoCamera size={28} weight="fill" className="text-white/50" />
          ) : (
            <LinkIcon size={28} weight="bold" className="text-white/50" />
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">
        {cls.description && <p className="text-xs text-slate-600 line-clamp-2">{cls.description}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="flex items-center gap-1"><CalendarBlank size={13} /> {cls.date}</span>
          <span className="flex items-center gap-1"><Clock size={13} /> {cls.start_time} - {cls.end_time}</span>
          <span className="flex items-center gap-1"><Users size={13} /> {cls.booked_count}/{cls.max_participants}</span>
          <VisibilityChip country={cls.country} countries={cls.visible_countries} />
          {cls.meeting_type === 'in_person' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold flex items-center gap-1" data-testid={`in-person-chip-${cls.class_id}`}>
              <MapPin size={10} weight="fill" /> In-Person
            </span>
          )}
        </div>
        {cls.meeting_type === 'in_person' && isBooked && !isMyClass && cls.venue_address && (
          <p className="text-[11px] text-slate-500 flex items-start gap-1" data-testid={`venue-address-${cls.class_id}`}>
            <MapPin size={12} className="text-amber-500 mt-0.5 shrink-0" weight="fill" /> {cls.venue_address}
          </p>
        )}
        {/* Remaining spots */}
        <div className="flex items-center justify-between">
          <div>
            {isFull ? (
              <span className="text-xs font-bold text-red-500">CLASS FULL</span>
            ) : (
              <span className="text-xs font-semibold text-emerald-600">{remaining} spot{remaining !== 1 ? 's' : ''} remaining</span>
            )}
            <div className="w-32 h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${isFull ? 'bg-red-500' : cls.booked_count / cls.max_participants > 0.7 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, (cls.booked_count / cls.max_participants) * 100)}%` }} />
            </div>
          </div>
          <span className="text-lg font-bold text-slate-900">
            {cls.price > 0 ? `${cls.currency === 'INR' ? '₹' : '$'}${cls.price}` : 'Free'}
          </span>
        </div>
        {/* Actions — strictly role-segregated */}
        <div className="flex gap-2 pt-1">
          {isMyClass ? (
            <span className="flex-1 text-xs text-slate-400 italic py-2 text-center" data-testid={`own-class-tag-${cls.class_id}`}>
              You are the host of this class
            </span>
          ) : isBooked ? (
            <>
              <Button size="sm" onClick={onJoin}
                className="flex-1 bg-pink-500 hover:bg-pink-600 rounded-xl text-xs h-9" data-testid={`join-class-${cls.class_id}`}>
                <Play size={14} className="mr-1" weight="fill" /> Join Class
              </Button>
              <Button size="sm" variant="outline" onClick={onCancel}
                className="border-red-200 text-red-500 hover:bg-red-50 rounded-xl text-xs h-9 px-3" data-testid={`cancel-class-${cls.class_id}`}>
                Cancel
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={onBook} disabled={isFull}
              className={`flex-1 rounded-xl text-xs h-9 ${isFull ? 'bg-slate-300 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'}`}
              data-testid={`book-class-${cls.class_id}`}>
              {isFull ? 'Full' : (user ? 'Book Spot' : 'Sign in to Book')}
            </Button>
          )}
        </div>
        {isBooked && !isMyClass && (
          <div className="border-t border-slate-100 pt-2">
            <AddToCalendar
              icsPath={`/api/live-classes/${cls.class_id}/calendar.ics`}
              title={`Live Session: ${cls.title}`}
              location={cls.venue_address || cls.external_link || 'Online'}
              description={`Instructor: ${cls.instructor || ''}\nSession: ${cls.session_type || ''}`}
              startISO={`${cls.date}T${cls.start_time}:00`}
              endISO={`${cls.date}T${cls.end_time}:00`}
              reminderText="Reminder set 30 min before class."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveClasses;

/* ============================
 * COUNTRY SINGLE-SELECT (browse filter)
 * ============================ */
const CountrySingleSelect = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const allCountries = useMemo(
    () => Country.getAllCountries().map(c => ({ code: c.isoCode, name: c.name, flag: c.flag })),
    []
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCountries.slice(0, 60);
    return allCountries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)).slice(0, 60);
  }, [allCountries, search]);
  const current = selected ? allCountries.find(c => c.code === selected.toUpperCase()) : null;
  return (
    <div className="relative flex-1 sm:flex-none sm:min-w-[240px]" data-testid="browse-country-select">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white text-left flex items-center justify-between gap-2 hover:border-pink-300"
        data-testid="browse-country-toggle">
        {current ? (
          <span className="inline-flex items-center gap-1.5 text-slate-800 font-medium">
            <span className="text-base">{current.flag}</span> {current.name}
          </span>
        ) : (
          <span className="text-slate-500 text-xs">All Countries (Global)</span>
        )}
        <Plus size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-45' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search country..." className="flex-1 text-xs outline-none"
              data-testid="browse-country-search" />
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            <button onClick={() => { onChange(''); setOpen(false); }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left hover:bg-slate-50 text-slate-600 font-medium"
              data-testid="browse-country-all">
              <Globe size={14} weight="bold" /> All Countries (Global)
            </button>
            {filtered.map(c => (
              <button key={c.code} type="button"
                onClick={() => { onChange(c.code); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left hover:bg-slate-50 ${selected === c.code ? 'bg-pink-50 text-pink-700 font-semibold' : 'text-slate-700'}`}
                data-testid={`browse-country-option-${c.code}`}>
                <span className="text-base">{c.flag}</span>
                <span className="flex-1">{c.name}</span>
                <span className="text-[10px] text-slate-400">{c.code}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-3">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================
 * COUNTRY + TIMEZONE CHIP (read-only)
 * ============================ */
const VisibilityChip = ({ countries, country, compact = false }) => {
  // Backwards compat: if old `countries` array passed, derive from first; otherwise use single `country`
  const code = (country || (Array.isArray(countries) && countries[0]) || '').toUpperCase();
  if (!code) {
    return (
      <span className={`inline-flex items-center gap-1 ${compact ? 'text-[10px] px-2 py-0.5' : 'text-xs'} text-slate-500`} data-testid="visibility-chip-global">
        <Globe size={compact ? 11 : 13} weight="bold" /> Global
      </span>
    );
  }
  const c = Country.getCountryByCode(code);
  const tz = c?.timezones?.[0]?.zoneName || '';
  return (
    <span className={`inline-flex items-center gap-1 ${compact ? 'text-[10px] px-2 py-0.5' : 'text-xs'} text-slate-600`} title={tz} data-testid="visibility-chip-country">
      <span className="text-base leading-none">{c?.flag || '🌐'}</span>
      <span className="font-medium">{c?.name || code}</span>
      {tz && !compact && <span className="text-slate-400">· {tz.split('/').pop().replace(/_/g, ' ')}</span>}
    </span>
  );
};

/* ============================
 * CLASS COUNTRY SINGLE-SELECT (with timezone hint)
 * ============================ */
const ClassCountrySelect = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const allCountries = useMemo(
    () => Country.getAllCountries().map(c => ({ code: c.isoCode, name: c.name, flag: c.flag, tz: c.timezones?.[0]?.zoneName || '' })),
    []
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCountries.slice(0, 60);
    return allCountries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)).slice(0, 60);
  }, [allCountries, search]);
  const current = selected ? allCountries.find(c => c.code === selected.toUpperCase()) : null;
  return (
    <div className="relative" data-testid="class-country-select">
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between gap-2 hover:border-pink-300 ${current ? 'bg-pink-50 border-pink-200' : 'bg-white border-slate-200'}`}
        data-testid="class-country-toggle">
        {current ? (
          <span className="inline-flex items-center gap-2 flex-1 min-w-0">
            <span className="text-lg">{current.flag}</span>
            <span className="text-slate-800 font-medium">{current.name}</span>
            {current.tz && <span className="text-[11px] text-slate-500 truncate">· {current.tz}</span>}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 text-slate-500 text-sm">
            <Globe size={14} weight="bold" /> Global (no specific timezone)
          </span>
        )}
        <Plus size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-45' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col" data-testid="class-country-popover">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search country..." className="flex-1 text-xs outline-none"
              data-testid="class-country-search" />
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            <button onClick={() => { onChange(''); setOpen(false); }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left hover:bg-slate-50 text-slate-600 font-medium"
              data-testid="class-country-global">
              <Globe size={14} weight="bold" /> Global (no specific timezone)
            </button>
            {filtered.map(c => (
              <button key={c.code} type="button"
                onClick={() => { onChange(c.code); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left hover:bg-slate-50 ${selected === c.code ? 'bg-pink-50 text-pink-700 font-semibold' : 'text-slate-700'}`}
                data-testid={`class-country-option-${c.code}`}>
                <span className="text-base">{c.flag}</span>
                <span className="flex-1">{c.name}</span>
                <span className="text-[10px] text-slate-400 truncate max-w-[120px]">{c.tz}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-3">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================
 * COUNTRY MULTI-SELECT (deprecated, kept for backwards compat — not currently rendered)
 * ============================ */
const CountryMultiSelect = ({ selected = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const allCountries = useMemo(
    () => Country.getAllCountries().map(c => ({ code: c.isoCode, name: c.name, flag: c.flag })),
    []
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCountries.slice(0, 60);
    return allCountries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)).slice(0, 60);
  }, [allCountries, search]);

  const toggle = (code) => {
    const up = code.toUpperCase();
    if (selected.includes(up)) {
      onChange(selected.filter(c => c !== up));
    } else {
      onChange([...selected, up]);
    }
  };

  const clearAll = () => onChange([]);

  const isGlobal = !selected || selected.length === 0;

  return (
    <div className="relative" data-testid="country-multi-select">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full min-h-[36px] px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white text-left flex items-center justify-between gap-2 hover:border-slate-300"
        data-testid="country-multi-select-toggle">
        <div className="flex flex-wrap gap-1 flex-1">
          {isGlobal ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-1.5 py-0.5">
              <Globe size={13} weight="bold" /> Global — visible to all countries
            </span>
          ) : (
            selected.map(code => {
              const c = allCountries.find(x => x.code === code);
              return (
                <span key={code} className="inline-flex items-center gap-1 bg-pink-100 text-pink-700 text-[11px] px-1.5 py-0.5 rounded-md font-medium">
                  {c?.flag} {c?.name || code}
                </span>
              );
            })
          )}
        </div>
        <Plus size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-45' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col" data-testid="country-multi-select-popover">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search countries..."
              className="flex-1 text-xs outline-none"
              data-testid="country-search-input"
            />
            {selected.length > 0 && (
              <button onClick={clearAll} className="text-[11px] text-pink-500 hover:text-pink-700 font-semibold" data-testid="clear-countries-btn">
                Clear
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            {filtered.map(c => {
              const checked = selected.includes(c.code);
              return (
                <button key={c.code} type="button" onClick={() => toggle(c.code)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left hover:bg-slate-50 ${checked ? 'bg-pink-50' : ''}`}
                  data-testid={`country-option-${c.code}`}>
                  <input type="checkbox" checked={checked} readOnly className="rounded text-pink-500 pointer-events-none" />
                  <span className="text-base">{c.flag}</span>
                  <span className={`flex-1 ${checked ? 'text-pink-700 font-semibold' : 'text-slate-700'}`}>{c.name}</span>
                  <span className="text-[10px] text-slate-400">{c.code}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-3">No matches</p>
            )}
          </div>
          <div className="p-2 border-t border-slate-100 text-[10px] text-slate-400 text-center">
            Leave empty for global visibility
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================
 * VISIBILITY CHIP — replaced by enhanced version above (single country + timezone)
 * ============================ */
